/**
 * Utility functions for Alignment Session components
 */

/**
 * Calculate criticality level (1-15) based on delta comparison with total
 * Used for dimension tables to show performance vs company average
 *
 * @param {number} elementDelta - The delta value for the element
 * @param {number} totalDelta - The delta value for total company
 * @param {boolean} isPositiveGood - Whether higher values are better (default: true)
 * @returns {number} Criticality level from 1 (worst) to 15 (best), 8 is neutral
 */
export const getDimensionCriticality = (
  elementDelta,
  totalDelta,
  isPositiveGood = true,
) => {
  if (totalDelta === 0) return 8;
  const diff = elementDelta - totalDelta;
  const ratio = diff / Math.abs(totalDelta);
  // Normalize to scale 1-15 (1 = much worse, 8 = neutral, 15 = much better)
  let level = Math.round(8 + ratio * 7);
  level = Math.max(1, Math.min(15, level));
  // Invert if lower values are better
  if (!isPositiveGood) level = 16 - level;
  return level;
};

/**
 * Calculate heatmap color level based on min/max values in the table
 * Used for margin heatmap to show relative performance across periods
 *
 * @param {number|null} value - The value to calculate criticality for
 * @param {Array<number|null>} allValues - All values in the heatmap
 * @returns {number} Criticality level from 1 (worst/min) to 15 (best/max)
 */
export const getHeatmapCriticality = (value, allValues) => {
  if (value === null) return 8;
  const validValues = allValues.filter((v) => v !== null);
  if (validValues.length === 0) return 8;
  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  if (min === max) return 8;
  // Normalize to scale 1-15 (1 = worst/min, 15 = best/max)
  const normalized = (value - min) / (max - min);
  return Math.max(1, Math.min(15, Math.round(1 + normalized * 14)));
};

/**
 * Get rotation angle for relevance arrow based on impact direction
 * Used for driver narratives to indicate strength and direction
 *
 * @param {string} relevance - Relevance level: "high", "medium", or "low"
 * @param {boolean} isNegative - Whether the impact is negative
 * @returns {number} Rotation angle in degrees
 */
export const getRelevanceArrow = (relevance, isNegative) => {
  const angles = { high: 90, medium: 60, low: 30 };
  const angle = angles[relevance] || 45;
  return isNegative ? -angle : angle;
};

/**
 * Format a number value for display in tables
 *
 * @param {number} value - The value to format
 * @param {number} decimals - Number of decimal places
 * @param {boolean} showSign - Whether to show + sign for positive values
 * @param {string} suffix - Suffix to add (e.g., "%", " pp")
 * @returns {string} Formatted string
 */
export const formatValue = (
  value,
  decimals = 1,
  showSign = true,
  suffix = "",
) => {
  if (value === null || value === undefined) return "-";
  const sign = showSign && value > 0 ? "+" : "";
  return `${sign}${value.toFixed(decimals)}${suffix}`;
};

/**
 * Format a number based on type (currency, percent, number)
 *
 * @param {number} value - The value to format
 * @param {string} type - Format type: "currency", "percent", or "number"
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted string
 */
export const formatNumber = (value, type = "number", decimals = 1) => {
  if (value === null || value === undefined) return "-";

  const isPositive = value >= 0;
  const sign = isPositive ? "+" : "";

  switch (type) {
    case "currency": {
      // Format as currency with thousands separator (no currency symbol)
      const absValue = Math.abs(value);
      if (absValue >= 1000000) {
        return `${sign}${(value / 1000000).toFixed(decimals)}M`;
      } else if (absValue >= 1000) {
        return `${sign}${(value / 1000).toFixed(decimals)}K`;
      }
      return `${sign}${value.toFixed(decimals)}`;
    }
    case "percent":
      return `${sign}${value.toFixed(decimals)}%`;
    default:
      return `${sign}${value.toFixed(decimals)}`;
  }
};

/**
 * Format year to show only last 2 digits
 *
 * @param {number} year - Full year (e.g., 2024)
 * @returns {string} Last 2 digits (e.g., "24")
 */
export const formatYearShort = (year) => {
  return String(year).slice(-2);
};

/**
 * Sort pages by order field
 *
 * @param {Array} pages - Array of page objects with order field
 * @returns {Array} Sorted array
 */
export const sortPagesByOrder = (pages) => {
  return [...pages].sort((a, b) => a.order - b.order);
};

/**
 * Find page index by ID in sorted pages array
 *
 * @param {Array} sortedPages - Sorted array of page objects
 * @param {number} pageId - ID of the page to find
 * @returns {number} Index of the page, or -1 if not found
 */
export const findPageIndex = (sortedPages, pageId) => {
  return sortedPages.findIndex((p) => p.id === pageId);
};

/**
 * Generate max ID from pages array
 *
 * @param {Array} pages - Array of page objects with id field
 * @returns {number} Maximum ID found
 */
export const getMaxPageId = (pages) => {
  return pages.reduce((max, p) => Math.max(max, p.id), 0);
};

/**
 * Generate max order from pages array
 *
 * @param {Array} pages - Array of page objects with order field
 * @returns {number} Maximum order found
 */
export const getMaxPageOrder = (pages) => {
  return pages.reduce((max, p) => Math.max(max, p.order), 0);
};

/**
 * Get month name abbreviation by index (0-11)
 *
 * @param {number} monthIndex - Month index (0-11)
 * @param {function} t - Translation function
 * @returns {string} Abbreviated month name
 */
export const getMonthAbbr = (monthIndex, t) => {
  // Translation keys are 1-based (months.1, months.2, etc.)
  const monthNumber = monthIndex + 1;
  return t(`months.${monthNumber}`);
};

/**
 * Format session's last data period for display
 *
 * @param {Object} session - Session object with year and period_value
 * @param {function} t - Translation function
 * @returns {string} Formatted period (e.g., "dez/24")
 */
export const formatSessionDataPeriod = (session, t) => {
  if (!session?.year || !session?.period_value) return "";
  const monthAbbr = getMonthAbbr(session.period_value - 1, t).toLowerCase();
  const yearShort = String(session.year).slice(-2);
  return `${monthAbbr}/${yearShort}`;
};

/**
 * Generate P&L title based on an array of [year, month] periods.
 * Formats the range as "mon/YY" or "mon/YY a mon/YY".
 *
 * @param {Array<[number,number]>|null} periods - Array of [year, month] pairs
 * @param {function} t - i18n translate function
 * @returns {string}
 */
export const getPnlTitleFromPeriods = (periods, t) => {
  if (!periods || periods.length === 0) return "P&L";

  const sorted = [...periods].sort(
    (a, b) => a[0] * 100 + a[1] - (b[0] * 100 + b[1]),
  );

  if (sorted.length === 1) {
    const [y, m] = sorted[0];
    return `${getMonthAbbr(m - 1, t)}/${String(y).slice(-2)}`;
  }

  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  return `${getMonthAbbr(first[1] - 1, t)}/${String(first[0]).slice(-2)} a ${getMonthAbbr(last[1] - 1, t)}/${String(last[0]).slice(-2)}`;
};

// =============================================================================
// Shared visual functions for dimension/ranking tables
// Used by DimensionTable, TopClientsSection (clients & products)
// =============================================================================

/**
 * Color scale for values based on fixed p.p. distance from total.
 * Uses absolute thresholds (not stdDev) so that the same delta value
 * always gets the same color regardless of which table it appears in.
 */
export const getValueColor = (value, totalValue) => {
  const diff = value - totalValue;
  if (diff <= -15) return "#c62828"; // dark red
  if (diff <= -7) return "#ef5350"; // red
  if (diff <= -2) return "#ff8a65"; // orange-red
  if (diff <= 2) return "#757575"; // gray (neutral)
  if (diff <= 7) return "#81c784"; // light green
  if (diff <= 15) return "#66bb6a"; // green
  return "#388e3c"; // dark green
};

/**
 * Calculate highlight styles (opacity and scale) based on fixed p.p.
 * distance from total, with share-based amplification.
 *
 * High-share elements get their effective distance amplified so that
 * even moderate deviations become visible (they matter more to the
 * business). Low-share elements are NOT suppressed — a large deviation
 * still gets full highlight regardless of share.
 *
 * Formula: amplifiedDiff = |delta - total| × (1 + share / 10)
 *
 * @param {number} value - Element's delta value (e.g. deltaVolume)
 * @param {number} totalValue - Total company's delta value
 * @param {number} share - Element's revenue share (0-100)
 * @returns {{ opacity: number, scale: number }}
 */
export const getHighlightFromDiff = (value, totalValue, share = 0) => {
  const absDiff = Math.abs(value - totalValue);
  const amplified = absDiff * (1 + Math.min(share, 25) / 10);
  if (amplified >= 25) return { opacity: 1.0, scale: 1.5 };
  if (amplified >= 15) return { opacity: 0.9, scale: 1.4 };
  if (amplified >= 7) return { opacity: 0.7, scale: 1.15 };
  if (amplified >= 3) return { opacity: 0.45, scale: 0.95 };
  if (amplified >= 1) return { opacity: 0.25, scale: 0.85 };
  return { opacity: 0.15, scale: 0.75 };
};

/**
 * Get highlight styles for a single element in a table row.
 * Uses fixed p.p. distance from total with share amplification.
 *
 * @param {object} element - Element with share and delta* fields
 * @param {object} totalElement - Total company element with delta* fields
 * @param {boolean} highlightEnabled - Whether highlight mode is on
 * @returns {{ volume, avgPrice, revenue, margin, marginPct }} - Style objects
 */
export const getElementHighlightStyles = (
  element,
  totalElement,
  highlightEnabled,
) => {
  const none = { opacity: 1, scale: 1 };
  if (!highlightEnabled) {
    return {
      volume: none,
      avgPrice: none,
      revenue: none,
      margin: none,
      marginPct: none,
    };
  }

  const share = element.share || 0;

  return {
    volume: getHighlightFromDiff(
      element.deltaVolume || 0,
      totalElement.deltaVolume || 0,
      share,
    ),
    avgPrice: getHighlightFromDiff(
      element.deltaAvgPrice || 0,
      totalElement.deltaAvgPrice || 0,
      share,
    ),
    revenue: getHighlightFromDiff(
      element.deltaRevenue || 0,
      totalElement.deltaRevenue || 0,
      share,
    ),
    margin: { opacity: 0.3, scale: 0.9 }, // Always faded in highlight mode
    marginPct: getHighlightFromDiff(
      element.deltaMarginPct || 0,
      totalElement.deltaMarginPct || 0,
      share,
    ),
  };
};

/**
 * Normalize legacy period_type values to the current 8-option keys.
 * Old sessions may have 'ytd', 'last_12m', 'last_month' stored in the DB.
 *
 * @param {string} raw - period_type from the backend
 * @returns {string} Normalized key matching getPeriodOptions()
 */
const LEGACY_PERIOD_MAP = {
  ytd: "ytd_vs_ytd",
  last_12m: "last_12m_vs_prev",
  last_month: "last_month_vs_ly",
};

export const normalizePeriodType = (raw) => {
  if (!raw) return "ytd_vs_ytd";
  return LEGACY_PERIOD_MAP[raw] || raw;
};
